import { apiGet, apiPut, apiDelete, apiPost } from "./client";
import type { ConnectorTemplate, SourceSystem } from "@ceo-cmd/shared";

export type { ConnectorTemplate };

export type SourceConnectionPublic = {
  userId: string;
  system: SourceSystem;
  connectionId: string;
  displayName?: string;
  authType?: string;
  status: "ok" | "stale" | "disconnected";
  configured: boolean;
  config?: Record<string, unknown>;
  lastSuccessAt?: string;
  lastError?: string;
  configuredAt?: string;
  updatedAt: string;
  /** Shown once after minting email push bearer. */
  accessTokenOnce?: string;
};

export type FeedIngestResult = {
  system: SourceSystem;
  connectionId: string;
  signals: number;
  upserted: number;
  lakeWritten?: number;
  synthesized?: number;
  archived?: number;
  done?: boolean;
  nextPage?: number;
  pagesFetched?: number;
  error?: string;
};

export async function listConnections(): Promise<SourceConnectionPublic[]> {
  const res = await apiGet<{ connections: SourceConnectionPublic[] }>(
    "/api/connections",
  );
  return res.connections ?? [];
}

/**
 * Connector catalogue from the base table — what the app supports and which
 * fields each connector needs. Cards without bespoke UI render from this.
 */
export async function listConnectorCatalog(): Promise<ConnectorTemplate[]> {
  const res = await apiGet<{ connectors: ConnectorTemplate[] }>(
    "/api/connections/catalog",
  );
  return res.connectors ?? [];
}

export async function saveConnection(
  system: SourceSystem,
  connectionId: string,
  body: {
    accessToken?: string;
    displayName?: string;
    config?: Record<string, unknown>;
    rotateBearer?: boolean;
  },
): Promise<SourceConnectionPublic> {
  const res = await apiPut<{ connection: SourceConnectionPublic }>(
    `/api/connections/${system}/${encodeURIComponent(connectionId)}`,
    body,
  );
  return res.connection;
}

export async function removeConnection(
  system: SourceSystem,
  connectionId = "default",
): Promise<void> {
  await apiDelete(
    `/api/connections/${system}/${encodeURIComponent(connectionId)}`,
  );
}

const SYNC_LOOP_CAP = 40;

/** Loop Apollo (or Sync-all) chunks until `done` or cap. */
export async function syncConnectionUntilDone(
  system: SourceSystem,
  connectionId: string,
  onProgress?: (partial: {
    iteration: number;
    signals: number;
    lakeWritten: number;
    done: boolean;
  }) => void,
): Promise<{
  signals: number;
  lakeWritten: number;
  synthesized: number;
  archived: number;
  iterations: number;
  last: FeedIngestResult;
}> {
  let signals = 0;
  let lakeWritten = 0;
  let synthesized = 0;
  let archived = 0;
  let last: FeedIngestResult = {
    system,
    connectionId,
    signals: 0,
    upserted: 0,
    done: true,
  };

  let iterations = 0;

  for (let i = 0; i < SYNC_LOOP_CAP; i++) {
    iterations = i + 1;
    last = await syncConnection(system, connectionId);
    if (last.error) break;
    signals += last.signals;
    lakeWritten += last.lakeWritten ?? 0;
    synthesized = last.synthesized ?? synthesized;
    archived = last.archived ?? archived;
    const done = last.done !== false;
    onProgress?.({
      iteration: iterations,
      signals,
      lakeWritten,
      done,
    });
    if (done) break;
  }

  return {
    signals,
    lakeWritten,
    synthesized,
    archived,
    iterations,
    last,
  };
}

export async function ingestConnectionsUntilDone(
  onProgress?: (partial: {
    iteration: number;
    snapshots: number;
    done: boolean;
  }) => void,
): Promise<{
  snapshots: number;
  upserted: number;
  synthesized: number;
  archived: number;
  sources: string[];
  feeds: FeedIngestResult[];
  done: boolean;
}> {
  let snapshots = 0;
  let synthesized = 0;
  let archived = 0;
  let sources: string[] = [];
  let feeds: FeedIngestResult[] = [];
  let done = true;

  for (let i = 0; i < SYNC_LOOP_CAP; i++) {
    const result = await ingestConnections();
    snapshots += result.snapshots;
    synthesized = result.synthesized ?? result.upserted;
    archived = result.archived ?? archived;
    sources = result.sources.length ? result.sources : sources;
    feeds = result.feeds;
    done = result.done !== false;
    onProgress?.({ iteration: i + 1, snapshots, done });
    if (done) {
      return {
        snapshots,
        upserted: synthesized,
        synthesized,
        archived,
        sources,
        feeds,
        done,
      };
    }
  }

  return {
    snapshots,
    upserted: synthesized,
    synthesized,
    archived,
    sources,
    feeds,
    done: false,
  };
}

export async function ingestConnections(): Promise<{
  snapshots: number;
  upserted: number;
  synthesized?: number;
  archived?: number;
  done?: boolean;
  sources: string[];
  feeds: FeedIngestResult[];
}> {
  return apiPost("/api/connections/ingest", {});
}

export async function syncConnection(
  system: SourceSystem,
  connectionId: string,
): Promise<FeedIngestResult> {
  return apiPost(
    `/api/connections/${system}/${encodeURIComponent(connectionId)}/sync`,
    {},
  );
}

export async function uploadCsvSignals(
  csv: string,
  filename?: string,
): Promise<{
  snapshots: number;
  upserted: number;
  sources: string[];
  batchId: string;
  status: "completed" | "failed";
  rowCounts: {
    total: number;
    accepted: number;
    rejected: number;
    upserted: number;
  };
  s3Skipped: boolean;
  downloads?: {
    originalUrl?: string;
    processedUrl?: string;
    exceptionsUrl?: string;
    manifestUrl?: string;
  };
  error?: string;
}> {
  return apiPost("/api/normalize/csv", { csv, filename });
}

export type CsvBatchSummary = {
  batchId: string;
  filename?: string;
  status: "processing" | "completed" | "failed";
  rowCounts: {
    total: number;
    accepted: number;
    rejected: number;
    upserted: number;
  };
  s3Skipped?: boolean;
  error?: string;
  createdAt: string;
  completedAt?: string;
};

export async function listCsvBatches(
  limit = 10,
): Promise<CsvBatchSummary[]> {
  const res = await apiGet<{ batches: CsvBatchSummary[] }>(
    `/api/normalize/csv/batches?limit=${limit}`,
  );
  return res.batches ?? [];
}

export async function getCsvBatch(batchId: string): Promise<{
  batch: CsvBatchSummary & { s3?: Record<string, string> };
  downloads?: {
    originalUrl?: string;
    processedUrl?: string;
    exceptionsUrl?: string;
    manifestUrl?: string;
  };
}> {
  return apiGet(`/api/normalize/csv/batches/${encodeURIComponent(batchId)}`);
}
